import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Calendar,
    ChevronLeft,
    Loader2,
    Zap,
    Moon,
    Brain,
    Utensils,
    Dumbbell,
    MessageSquare,
    CheckCircle,
    History,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function AthleteCheckInPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);

    // Form state - using 1-5 scale for metrics
    const [energy, setEnergy] = useState(3);
    const [sleep, setSleep] = useState(3);
    const [stress, setStress] = useState(3);
    const [diet, setDiet] = useState(3);
    const [training, setTraining] = useState(3);
    const [notes, setNotes] = useState('');

    // Fetch check-in history
    const { data: history, isLoading } = useQuery({
        queryKey: ['athlete-checkins', user?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('check_ins')
                .select('*')
                .eq('student_id', user?.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data;
        },
        enabled: !!user?.id,
    });

    const submitCheckInMutation = useMutation({
        mutationFn: async (data: any) => {
            const { error } = await supabase
                .from('check_ins')
                .insert([{
                    student_id: user?.id,
                    check_in_date: new Date().toISOString(),
                    energy_level: data.energy,
                    sleep_quality: data.sleep,
                    stress_level: data.stress,
                    nutrition_adherence: data.diet,
                    workout_adherence: data.training,
                    notes: data.notes
                }]);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['athlete-checkins'] });
            setShowForm(false);
            resetForm();
        },
    });

    const resetForm = () => {
        setEnergy(3);
        setSleep(3);
        setStress(3);
        setDiet(3);
        setTraining(3);
        setNotes('');
    };

    const RatingInput = ({ label, icon: Icon, value, onChange, colorClass }: any) => (
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 border border-gray-100 dark:border-gray-800 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <span className="font-bold text-gray-900 dark:text-white">{label}</span>
            </div>
            <div className="flex justify-between gap-1">
                {[1, 2, 3, 4, 5].map((num) => (
                    <button
                        key={num}
                        type="button"
                        onClick={() => onChange(num)}
                        className={`flex-1 py-3 px-1 rounded-xl text-lg font-bold transition-all ${value === num
                                ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/20 scale-105'
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                    >
                        {num}
                    </button>
                ))}
            </div>
            <div className="flex justify-between mt-2 px-1">
                <span className="text-[10px] text-gray-400 font-medium">Muito Baixo</span>
                <span className="text-[10px] text-gray-400 font-medium">Excelente</span>
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-20">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Check-in Semanal</h1>
            </div>

            {!showForm && (
                <div className="space-y-8">
                    {/* Welcome Card */}
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-2">Como foi sua semana?</h2>
                            <p className="text-emerald-50 text-sm max-w-xs mb-6">Seu feedback ajuda a ajustar seu plano e garantir melhores resultados.</p>
                            <button
                                onClick={() => setShowForm(true)}
                                className="px-6 py-3 bg-white text-emerald-600 rounded-2xl font-bold text-sm shadow-lg hover:bg-emerald-50 transition-colors"
                            >
                                Iniciar Check-in
                            </button>
                        </div>
                        <CheckCircle className="absolute -bottom-4 -right-4 w-32 h-32 text-white/10 rotate-12" />
                    </div>

                    {/* History */}
                    <div className="space-y-4">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <History className="w-5 h-5 text-gray-400" />
                            Histórico de Feedbacks
                        </h2>

                        {isLoading ? (
                            <div className="space-y-3">
                                {[1, 2].map(i => <div key={i} className="h-24 bg-gray-50 dark:bg-gray-800 rounded-2xl animate-pulse"></div>)}
                            </div>
                        ) : history && history.length > 0 ? (
                            <div className="grid gap-4">
                                {history.map((check: any) => (
                                    <div key={check.id} className="bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-100 dark:border-gray-800 shadow-sm">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-primary-600" />
                                                <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                    {new Date(check.check_in_date).toLocaleDateString('pt-BR')}
                                                </span>
                                            </div>
                                            <span className="text-xs bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded-full font-bold">
                                                Enviado
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-5 gap-2">
                                            {[
                                                { icon: Zap, val: check.energy_level },
                                                { icon: Moon, val: check.sleep_quality },
                                                { icon: Brain, val: check.stress_level },
                                                { icon: Utensils, val: check.nutrition_adherence },
                                                { icon: Dumbbell, val: check.workout_adherence },
                                            ].map((m, i) => (
                                                <div key={i} className="flex flex-col items-center gap-1">
                                                    <m.icon className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-bold">{m.val}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {check.notes && (
                                            <div className="mt-4 pt-4 border-t border-gray-50 dark:border-gray-800">
                                                <p className="text-xs text-gray-500 italic">"{check.notes}"</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/20 rounded-3xl border-2 border-dashed">
                                <p className="text-gray-500 text-sm">Ainda não há check-ins realizados.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showForm && (
                <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
                    <div className="grid gap-4 md:grid-cols-2">
                        <RatingInput
                            label="Nível de Energia"
                            icon={Zap}
                            value={energy}
                            onChange={setEnergy}
                            colorClass="bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20"
                        />
                        <RatingInput
                            label="Qualidade do Sono"
                            icon={Moon}
                            value={sleep}
                            onChange={setSleep}
                            colorClass="bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20"
                        />
                        <RatingInput
                            label="Nível de Estresse"
                            icon={Brain}
                            value={stress}
                            onChange={setStress}
                            colorClass="bg-purple-50 text-purple-600 dark:bg-purple-900/20"
                        />
                        <RatingInput
                            label="Adesão à Dieta"
                            icon={Utensils}
                            value={diet}
                            onChange={setDiet}
                            colorClass="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20"
                        />
                        <RatingInput
                            label="Adesão aos Treinos"
                            icon={Dumbbell}
                            value={training}
                            onChange={setTraining}
                            colorClass="bg-primary-50 text-primary-600 dark:bg-primary-900/20"
                        />

                        <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 border border-gray-100 dark:border-gray-800 shadow-sm md:col-span-1">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                                    <MessageSquare className="w-5 h-5 text-gray-600" />
                                </div>
                                <span className="font-bold text-gray-900 dark:text-white">Observações</span>
                            </div>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Conte como foi sua semana..."
                                className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm focus:ring-2 focus:ring-primary-500 min-h-[120px]"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4 pt-6">
                        <button
                            onClick={() => setShowForm(false)}
                            className="flex-1 py-4 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-2xl font-bold hover:bg-gray-200 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={() => submitCheckInMutation.mutate({ energy, sleep, stress, diet, training, notes })}
                            disabled={submitCheckInMutation.isPending}
                            className="flex-[2] py-4 bg-primary-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all active:scale-[0.98]"
                        >
                            {submitCheckInMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                            Enviar Check-in
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
